/**
 * An element that declares its own editing host — `<schmancy-typography-field>`'s
 * inner editable, CodeMirror's `.cm-content` inside `<schmancy-editor>`, a
 * consumer's own rich-text region. The engine tabs into these, so a trap that
 * claims to be authoritative over the tab order has to list them.
 */
const EDITING_HOST_SELECTOR = '[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]'

/**
 * Only a details element's own first summary takes focus; a loose `<summary>`
 * listed here would take a silent no-op `.focus()` and pin the trap on it.
 */
const FOCUSABLE_SELECTOR = `a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), details > summary:first-of-type, iframe, audio[controls], video[controls], ${EDITING_HOST_SELECTOR}, [tabindex]:not([tabindex="-1"])`

/**
 * The elements the flat tree renders under `node`, in rendered order: a host's
 * shadow-root children, a slot's assigned elements (its fallback content while
 * nothing is assigned), otherwise its own children. A host's light children
 * appear only where a slot places them, so one no slot takes renders nowhere.
 */
function renderedChildren(node: Element): Element[] {
	if (node.shadowRoot) return [...node.shadowRoot.children]
	if (node instanceof HTMLSlotElement) return node.assignedElements({ flatten: true })
	return [...node.children]
}

/**
 * The tab stops under `root`, in the order the engine's own Tab visits them:
 * a depth-first walk of the flat tree, pierced through nested shadow roots and
 * following slot assignment. A schmancy component nests its real control
 * several shadow boundaries down and places a consumer's slotted controls
 * between its own, so neither a plain `querySelectorAll` nor a walk of the
 * light tree yields the order a person sees. A control that is not rendered,
 * disabled or inert is no stop: the overlay's Tab trap moves focus itself on
 * every Tab, and `.focus()` on such a control is a silent no-op that holds
 * focus where it was, press after press.
 */
export function focusableDescendants(root: HTMLElement | undefined | null): HTMLElement[] {
	if (!root) return []
	const found: HTMLElement[] = []
	// `inert` disables focus for the whole subtree it sits on, across shadow
	// boundaries, while every descendant keeps reporting `tabIndex >= 0` and
	// carries no `inert` attribute of its own (measured) — so inertness is
	// carried DOWN the walk rather than read off each candidate.
	const walk = (node: Element, inert: boolean): void => {
		for (const child of renderedChildren(node)) {
			if (child instanceof HTMLElement) {
				const childInert = inert || child.hasAttribute('inert')
				// Chromium reports `tabIndex === -1` on a bare editing host (measured)
				// while still tabbing into it, so for an editing host the opt-out that
				// counts is the author's own `tabindex="-1"`, not the IDL value that
				// would silently drop every un-annotated one.
				const tabbable = child.matches(EDITING_HOST_SELECTOR)
					? child.getAttribute('tabindex') !== '-1'
					: child.tabIndex !== -1
				// A delegatesFocus host is no tab stop of its own; listing it beside its
				// control makes a backward step land on the host, which hands focus
				// straight back.
				const delegates = child.shadowRoot?.delegatesFocus === true
				if (
					child.matches(FOCUSABLE_SELECTOR) &&
					!childInert &&
					tabbable &&
					!delegates &&
					!child.matches(':disabled') &&
					child.checkVisibility({ checkVisibilityCSS: true })
				) {
					found.push(child)
				}
				walk(child, childInert)
			}
		}
	}
	walk(root, root.hasAttribute('inert'))
	return found
}

/**
 * The first focusable descendant of `root` (piercing shadow roots) — the
 * real element `.focus()` should land on, since a wrapper custom element
 * (`schmancy-menu-item`, `schmancy-list-item`) with no `delegatesFocus`
 * shadow root and no `tabindex` of its own is not itself focusable.
 */
export function firstFocusableDescendant(root: HTMLElement | undefined | null): HTMLElement | null {
	return focusableDescendants(root)[0] ?? null
}

/**
 * The first `[autofocus]`-bearing element the flat tree renders under `root`,
 * pierced through nested shadow roots — mirrors `focusableDescendants`'s walk. A consumer's
 * `autofocus` attribute routinely lands on a light-DOM child of its OWN Lit
 * component (its own shadow root), several boundaries down from the
 * overlay's mounted surface, so a plain `querySelector('[autofocus]')` can
 * never reach it.
 */
function firstAutofocusElement(root: HTMLElement | undefined | null): HTMLElement | null {
	if (!root) return null
	let found: HTMLElement | null = null
	const walk = (node: Element, inert: boolean): void => {
		if (found) return
		for (const child of renderedChildren(node)) {
			if (found) return
			if (child instanceof HTMLElement) {
				const childInert = inert || child.hasAttribute('inert')
				if (child.hasAttribute('autofocus') && !childInert) {
					found = child
					return
				}
				walk(child, childInert)
			}
		}
	}
	walk(root, root.hasAttribute('inert'))
	return found
}

/**
 * The element opening-focus should actually call `.focus()` on for an
 * `[autofocus]` opt-in anywhere under `root` (pierced through nested shadow
 * roots — see `firstAutofocusElement`). When the `autofocus`-bearing element
 * is itself not focusable — a bare custom-element host with no `tabindex`
 * and no `delegatesFocus` shadow root (`schmancy-menu-item`,
 * `schmancy-list-item`) — resolves to its own first focusable descendant,
 * the same fallback `firstFocusableDescendant`'s doc comment already names
 * for that case. A host whose shadow root delegates focus (e.g.
 * `schmancy-icon-button`) is directly focusable even with `tabIndex === -1`,
 * since its own `focus()` override reaches the real control itself. An
 * explicit `tabindex="-1"` also accepts programmatic focus while remaining
 * outside the Tab trap's list of sequential stops.
 */
export function firstAutofocusTarget(root: HTMLElement | undefined | null): HTMLElement | null {
	const auto = firstAutofocusElement(root)
	if (!auto) return null
	const directlyFocusable = auto.tabIndex !== -1 || auto.getAttribute('tabindex') === '-1' || auto.shadowRoot?.delegatesFocus === true
	return directlyFocusable ? auto : (firstFocusableDescendant(auto) ?? auto)
}

/**
 * `document.activeElement` pierced through shadow roots — the platform
 * property stops at the first shadow boundary and reports only the
 * shadow-hosting element, not the actually-focused descendant inside it.
 */
export function deepActiveElement(): Element | null {
	let el = document.activeElement
	while (el?.shadowRoot?.activeElement) {
		el = el.shadowRoot.activeElement
	}
	return el
}
